import { TailSpin } from 'react-loader-spinner'
import { TestItem } from '../../components/TestItem'
import { Blue300 } from '../../lib/colors'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import {
  SearchTestFragment,
  useGetShippingTestsByTagSubscription,
} from '../../generated/urql.client'
import { useGetTagSubscription } from '../../generated/urql.anonymous'
import { useI18n } from '../../provider/i18n'
import { AuthContext } from '../../provider/auth'
import { BasketContext, isShippingTestBasketItem } from '../../provider/basket'
import { useAtomValue } from 'jotai'
import { profilesSubscriptionAtom } from '../../store/subscription.atoms'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../components/ProfileSelectorModal'

const ShippingTestsByTag = () => {
  const [isInited, setIsInited] = useState(false)
  const params = useParams<{ tagId: string }>()
  const i18n = useI18n()
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)
  const basket = useContext(BasketContext)
  const { isLogged } = useContext(AuthContext)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [{ data: testsData, fetching }] = useGetShippingTestsByTagSubscription(
    {
      pause: !params.tagId,
      variables: {
        tag: params.tagId || '',
        limit: 1000,
        offset: 0,
      },
    }
  )
  const [{ data: tagData }] = useGetTagSubscription({
    pause: !params.tagId,
    variables: {
      id: params.tagId || '',
    },
  })

  const tests = testsData?.search_test_shipping_tag || []
  const tag = tagData?.tag_by_pk || { id: '', tag: '' }

  const isFetching = useMemo(() => fetching && !isInited, [fetching, isInited])

  useEffect(() => {
    setIsInited(false)
  }, [params.tagId])

  useEffect(() => {
    if (!isFetching) {
      setIsInited(true)
    }
  }, [isFetching])

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!isLogged || profilesCount === 0) {
        basket.addItem({ test, shouldShipToClient: true })
      } else if (profilesCount === 1) {
        basket.addItem({
          test,
          profile: profilesData?.client_profile[0],
          shouldShipToClient: true,
        })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile, shouldShipToClient: true })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isShippingTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [isLogged, basket, profilesData?.client_profile]
  )

  if (isFetching) {
    return (
      <TailSpin
        color={Blue300}
        height={32}
        width={32}
        wrapperClass="flex justify-center p-8"
      />
    )
  }

  return (
    <div className="mb-16">
      <div className="text-blue-700 md:mx-5 my-5 gap-1 items-center">
        <span>
          <Link to="/shipping-tests">
            {i18n.t('screens.catalog.index.shippingTests.header')}
          </Link>
          {' > '}
          {tag.tag}
        </span>
      </div>
      <ul className="md:mx-5 flex flex-col gap-5 mb-10">
        {tests.map((test) => (
          <li key={test.id}>
            <TestItem
              test={test as SearchTestFragment}
              variant="green"
              onTestAddClick={() => onTestAddClick(test as SearchTestFragment)}
              showAddButton={
                isLogged
                  ? basket.items
                      .filter(isShippingTestBasketItem)
                      .filter(({ test: { id } }) => test.id === id).length <
                    (profilesData?.client_profile.length || 0)
                  : !basket.items.find(
                      (item) =>
                        isShippingTestBasketItem(item) &&
                        test.id === item.test.id
                    )
              }
            />
          </li>
        ))}
      </ul>

      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}

export default ShippingTestsByTag
